import { useState, useEffect, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../context/AuthContext";
import { signUp } from "../services/authService";
import { IconChevronLeft } from "@tabler/icons-react";
import VerificationPopup from "../components/VerificationPopup";
import { AlertIcon } from "../components/Icons";
import "../components/DatePicker.css";
import {
  evaluatePassword,
  isPasswordValid,
  evaluateEmail,
  isEmailValid,
  getMinimumDobDate,
  validateSignUpForm,
} from "../lib/validators";
import { createLogger } from "../lib/logger";

const log = createLogger("SignUp");

// ─── Shared styles ────────────────────────────────────────────────────────────
const FONT = { fontFamily: "DM Sans, sans-serif" };

const labelClass = "font-medium text-base text-[#0c2624] tracking-[0.4px]";

const inputClass =
  "w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-base " +
  "text-[#1e1e1e] placeholder:text-[#b3b3b3] outline-none transition-all " +
  "focus:border-[#1e5e5b] focus:ring-[3px] focus:ring-[#1e5e5b]/10";

// ─── Reusable criterion row ───────────────────────────────────────────────────
const CriterionRow = ({ met, label }: { met: boolean; label: string }) => (
  <li className={`text-xs ${met ? "text-green-600" : "text-gray-500"}`} style={FONT}>
    {met ? "✓" : "✗"} {label}
  </li>
);

// ─── Component ───────────────────────────────────────────────────────────────
const SignUp = () => {
  // ── Form values ───────────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState<Date | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeAnonymisedData, setAgreeAnonymisedData] = useState(false);
  const [agreeAgeAndBeta, setAgreeAgeAndBeta] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false); // optional

  // ── UI state ──────────────────────────────────────────────────────────────
  const [error, setError] = useState("");
  const [emailInUseError, setEmailInUseError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);

  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // ── Derived validation ────────────────────────────────────────────────────
  const passwordCriteria = useMemo(() => evaluatePassword(password), [password]);
  const emailCriteria = useMemo(() => evaluateEmail(email), [email]);
  const allPasswordCriteriaMet = isPasswordValid(passwordCriteria);
  const allEmailCriteriaMet = isEmailValid(emailCriteria);
  const passwordsMatch = password === confirmPassword;

  // agreeMarketing is intentionally excluded — it's optional and never blocks submission
  const isFormComplete =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    dob !== null &&
    allEmailCriteriaMet &&
    allPasswordCriteriaMet &&
    passwordsMatch &&
    agreeTerms &&
    agreeAnonymisedData &&
    agreeAgeAndBeta;

  const missingFields = useMemo(() => {
    const items: string[] = [];
    if (!firstName.trim()) items.push("first name");
    if (!lastName.trim()) items.push("last name");
    if (!dob) items.push("date of birth");
    if (!allEmailCriteriaMet) items.push("valid email");
    if (!allPasswordCriteriaMet) items.push("password requirements");
    if (!passwordsMatch && confirmPassword.length > 0) items.push("matching passwords");
    if (!agreeTerms) items.push("terms & conditions");
    if (!agreeAnonymisedData || !agreeAgeAndBeta) items.push("age & beta confirmation");
    return items;
  }, [
    firstName, lastName, dob,
    allEmailCriteriaMet, allPasswordCriteriaMet,
    passwordsMatch, confirmPassword,
    agreeTerms, agreeAnonymisedData, agreeAgeAndBeta,
  ]);

  // ── Persist draft to sessionStorage ──────────────────────────────────────
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("signUpFormData");
      if (!saved) return;
      const data = JSON.parse(saved);
      setFirstName(data.firstName || "");
      setLastName(data.lastName || "");
      setDob(data.dob ? new Date(data.dob) : null);
      setEmail(data.email || "");
      setAgreeTerms(data.agreeTerms ?? false);
      setAgreeAnonymisedData(data.agreeAnonymisedData ?? false);
      setAgreeAgeAndBeta(data.agreeAgeAndBeta ?? false);
      setAgreeMarketing(data.agreeMarketing ?? false);
    } catch (err) {
      console.warn("Failed to restore sign-up draft from sessionStorage");
      log.warn("Failed to restore sign-up draft from sessionStorage", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      "signUpFormData",
      JSON.stringify({
        firstName,
        lastName,
        dob: dob ? dob.toISOString() : null,
        email,
        agreeTerms,
        agreeAnonymisedData,
        agreeAgeAndBeta,
        agreeMarketing,
      }),
    );
  }, [firstName, lastName, dob, email, agreeTerms, agreeAnonymisedData, agreeAgeAndBeta, agreeMarketing]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const validationError = validateSignUpForm({
      dob,
      email,
      password,
      confirmPassword,
      agreeTerms,
      agreeAnonymisedData,
      agreeAgeAndBeta,
    });

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    const result = await signUp({
      firstName,
      lastName,
      dob: dob!,
      email,
      password,
      agreeTerms,
      agreeAnonymisedData,
      agreeAgeAndBeta,
      agreeMarketing,
      continueUrl: `${window.location.origin}/login`,
    });

    setIsSubmitting(false);

    if (!result.ok) {
      if (result.error.toLowerCase().includes("already in use")) {
        setEmailInUseError(result.error);
      } else {
        setError(result.error);
      }
      return;
    }

    setShowVerificationPopup(true);
    sessionStorage.removeItem("signUpFormData");
  };

  const resetForm = () => {
    setFirstName(""); setLastName(""); setDob(null);
    setEmail(""); setPassword(""); setConfirmPassword("");
    setAgreeTerms(false); setAgreeAnonymisedData(false); setAgreeAgeAndBeta(false);
    setAgreeMarketing(false);
    setError(""); setEmailInUseError("");
    setFirstNameTouched(false); setLastNameTouched(false);
    sessionStorage.removeItem("signUpFormData");
  };

  const handlePopupClose = () => {
    setShowVerificationPopup(false);
    resetForm();
    navigate("/login");
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  if (loading) return <p>Loading...</p>;
  if (user?.emailVerified) return <Navigate to="/dashboard" />;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {showVerificationPopup && (
        <VerificationPopup email={email} onClose={handlePopupClose} />
      )}

      <div className="min-h-screen w-full flex flex-col items-center bg-[#fcf9f7] py-8 px-6 gap-6">
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 bg-transparent border-0 font-semibold text-sm text-[#1e1e1e] cursor-pointer self-start transition-opacity hover:opacity-70"
          style={FONT}
        >
          <IconChevronLeft size={16} />
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="flex flex-col gap-3">
          <h1 className="font-semibold text-[32px] text-[#1e1e1e] leading-tight m-0" style={FONT}>
            Join BodyOwn
          </h1>
          <p className="font-medium text-xl text-[#757575] leading-snug m-0" style={FONT}>
            Tell us a little about you so we can support your recovery journey.
          </p>
        </div>

        {/* Form */}
        <div className="w-full max-w-[680px] flex flex-col gap-6">
          {error && (
            <div className="flex items-center gap-3 p-3 bg-amber-100 rounded-lg">
              <AlertIcon />
              <p className="text-amber-800 text-sm m-0" style={FONT}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSignUp} className="flex flex-col gap-6">
            {/* First name */}
            <div className="flex flex-col gap-1.5">
              <label className={labelClass} style={FONT}>First name</label>
              <input
                type="text"
                placeholder="Please enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={() => setFirstNameTouched(true)}
                required
                className={inputClass}
                style={FONT}
              />
              {firstNameTouched && !firstName.trim() && (
                <p className="text-xs text-gray-500 mt-1 m-0" style={FONT}>✗ First name is required</p>
              )}
            </div>

            {/* Last name */}
            <div className="flex flex-col gap-1.5">
              <label className={labelClass} style={FONT}>Last name</label>
              <input
                type="text"
                placeholder="Please enter your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={() => setLastNameTouched(true)}
                required
                className={inputClass}
                style={FONT}
              />
              {lastNameTouched && !lastName.trim() && (
                <p className="text-xs text-gray-500 mt-1 m-0" style={FONT}>✗ Last name is required</p>
              )}
            </div>

            {/* Date of birth */}
            <div className="flex flex-col gap-1.5">
              <label className={labelClass} style={FONT}>Date of birth</label>
              <DatePicker
                selected={dob}
                onChange={(date: Date | null) => setDob(date)}
                maxDate={getMinimumDobDate()}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                placeholderText="dd/mm/yyyy"
                dateFormat="dd/MM/yyyy"
                className={inputClass}
                wrapperClassName="w-full"
              />
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label className={labelClass} style={FONT}>Email</label>
              <input
                type="email"
                placeholder="Please enter your email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailInUseError(""); }}
                required
                className={inputClass}
                style={FONT}
              />
              {email.length > 0 && !allEmailCriteriaMet && (
                <ul className="flex flex-col gap-0.5 mt-1 list-none p-0 m-0">
                  <CriterionRow met={emailCriteria.atSymbol} label="Contains @ symbol" />
                  <CriterionRow met={emailCriteria.domain} label="Has a valid domain (e.g. example.com)" />
                  <CriterionRow met={emailCriteria.noSpaces} label="No spaces" />
                </ul>
              )}
              {emailInUseError && (
                <p className="text-xs text-gray-500 mt-1 m-0" style={FONT}>✗ {emailInUseError}</p>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <label className={labelClass} style={FONT}>Password</label>
              <input
                type="password"
                placeholder="Please enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={inputClass}
                style={FONT}
              />
              {password.length > 0 && !allPasswordCriteriaMet && (
                <ul className="flex flex-col gap-0.5 mt-1 list-none p-0 m-0">
                  <CriterionRow met={passwordCriteria.length}       label="At least 8 characters" />
                  <CriterionRow met={passwordCriteria.uppercase}    label="One uppercase letter" />
                  <CriterionRow met={passwordCriteria.lowercase}    label="One lowercase letter" />
                  <CriterionRow met={passwordCriteria.number}       label="One number" />
                  <CriterionRow met={passwordCriteria.specialChar} label="One special character (e.g. ! @ # $ %)" />
                </ul>
              )}
            </div>

            {/* Confirm password */}
            <div className="flex flex-col gap-1.5">
              <label className={labelClass} style={FONT}>Confirm password</label>
              <input
                type="password"
                placeholder="Please re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={inputClass}
                style={FONT}
              />
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-red-500 mt-1 m-0" style={FONT}>✗ Passwords do not match</p>
              )}
              {confirmPassword.length > 0 && passwordsMatch && allPasswordCriteriaMet && (
                <p className="text-xs text-green-600 mt-1 m-0" style={FONT}>✓ Passwords match</p>
              )}
            </div>

            {/* 1. Terms and conditions */}
            <div className="flex items-start gap-3">
              <input
                id="agree-terms"
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="w-5 h-5 mt-0.5 accent-[#1e5e5b] cursor-pointer"
              />
              <label htmlFor="agree-terms" className="text-sm text-[#1e1e1e] cursor-pointer" style={FONT}>
                I agree to the{" "}
                <Link to="/terms-and-conditions" target="_blank" className="text-[#1e5e5b] underline">
                  terms and conditions.
                </Link>
              </label>
            </div>

            {/* 2. Age, beta, feedback contact & anonymised data — single combined consent */}
            <div className="flex items-start gap-3">
              <input
                id="agree-age-beta-data"
                type="checkbox"
                checked={agreeAnonymisedData && agreeAgeAndBeta}
                onChange={(e) => {
                  setAgreeAnonymisedData(e.target.checked);
                  setAgreeAgeAndBeta(e.target.checked);
                }}
                className="w-5 h-5 mt-0.5 accent-[#1e5e5b] cursor-pointer shrink-0"
              />
              <label htmlFor="agree-age-beta-data" className="text-sm text-[#1e1e1e] cursor-pointer" style={FONT}>
                I confirm that I am 18 or over. I understand that this is a free beta/testing app, not a paid subscription service, and that features may be incomplete, change, or be withdrawn at any time. I agree and understand that BodyOwn may contact me about feedback opportunities to help improve the experience, and that my data may be used in anonymised/aggregated form to improve the service.
              </label>
            </div>

            {/* 3. Optional marketing consent */}
            <div className="flex items-start gap-3">
              <input
                id="agree-marketing"
                type="checkbox"
                checked={agreeMarketing}
                onChange={(e) => setAgreeMarketing(e.target.checked)}
                className="w-5 h-5 mt-0.5 accent-[#1e5e5b] cursor-pointer shrink-0"
              />
              <label htmlFor="agree-marketing" className="text-sm text-[#1e1e1e] cursor-pointer" style={FONT}>
               I confirm that I may occasionally receive BodyOwn updates via email and WhatsApp. (Optional) 
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || !isFormComplete}
              className={`w-full py-4 rounded-full font-semibold text-base transition-all ${
                isSubmitting || !isFormComplete
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#1e5e5b] text-white cursor-pointer hover:bg-[#164b49] hover:-translate-y-0.5 hover:shadow-lg"
              }`}
              style={FONT}
            >
              {isSubmitting ? "Creating your account..." : "Continue"}
            </button>

            {/* Still-needed hint */}
            {missingFields.length > 0 && (
              <p className="text-xs text-[#757575] text-center m-0 -mt-3" style={FONT}>
                Still needed: {missingFields.join(", ")}
              </p>
            )}
          </form>

          {/* Login link */}
          <div className="flex items-center justify-center gap-2 pt-4">
            <p className="text-base text-[#1e1e1e] m-0" style={FONT}>Already have an account?</p>
            <Link to="/login" className="text-[#1e5e5b] font-semibold text-base underline" style={FONT}>
              Login
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;